import React from 'react'
import _get from 'lodash/get'
import _set from 'lodash/set'
import { List, Map, fromJS } from 'immutable'
import { useQuery } from '@apollo/client'
import {
  PERSON_IMAGE_LIST,
  CONTENT_IMAGE_TYPE_HEADSHOT,
  getOrderedContent,
  getFormattedContentData,
} from 'services/content'
import { GET_PERSONS } from 'services/content/query'
import { APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST } from 'services/apollo-graphql'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from 'components/Picture'
import { get as getConfig } from 'config'
import { TYPE_TAG_YOGA_STYLE } from 'services/content-type'
import {
  parse as parseQuery,
  stringify as stringifyQuery,
} from 'services/query-string'
import Sherpa, { TYPE_LARGE } from 'components/Sherpa'
import Link from 'components/Link'
import { H6 } from 'components/Heading'

const config = getConfig()
const optimizedImagesEnabled = !!_get(config, ['features', 'optimizedImages'])

/**
 * The yoga teachers page tile component
 * @param {Object} props - The props
 * @param {Map} props.item - The tile item data
 * @returns {JSX.Element} The tile component
 */
function YogaTeachersPageTile (props) {
  const { data } = props

  const title = data.get('title', '')
  const references = data.get('references', List()) || List()
  const yogaStyles = references
    .filter((r) => r.get('type') === TYPE_TAG_YOGA_STYLE)
    .map((r) => r.get('title'))
    .join(', ')

  const images = data.get('images', List()) || List()
  const imageData = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_HEADSHOT) || Map()
  const image = imageData.get('url', '')
  const imageWidth = '144'
  const imageHeight = '144'

  let imageUrl = ''
  let fallbackImageUrl = ''

  try {
    if (optimizedImagesEnabled) {
      const fallbackImageUrlObject = new URL(image)
      const imageUrlObject = new URL(image)
      const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
      const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
      const fallbackImageOptions = {
        ...parsedFallbackImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'jpg',
        compress: 'auto',
      }
      const imageOptions = {
        ...parsedImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'webp',
        q: '100',
      }

      const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
      const stringifiedImageQuery = stringifyQuery(imageOptions)

      // set the search string back onto the fallback URL
      _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

      // set the search string back onto the image URL
      _set(imageUrlObject, 'search', stringifiedImageQuery)

      fallbackImageUrl = fallbackImageUrlObject.toString()
      imageUrl = imageUrlObject.toString()
    }
  } catch (e) {
    fallbackImageUrl = image
    imageUrl = image
  }

  return (
    <div className="yoga-teachers-page__tile">
      <Link
        to={`${data.get('path', '')}?filter-set=yoga`}
      >
        <div className="yoga-teachers-page__tile-hero">
          <div className="yoga-teachers-page__tile-hero-bg"/>
          <div className="yoga-teachers-page__tile-hero-image">
            {
              optimizedImagesEnabled
                ? <Picture
                  fallbackSrc={fallbackImageUrl}
                  width={imageWidth}
                  height={imageHeight}
                  alt={title}
                  loading={IMAGE_LOADING_LAZY}
                  sources={[
                    {
                      type: IMAGE_TYPE_WEBP,
                      srcSet: imageUrl,
                    },
                  ]}
                />
                : <img
                  src={image}
                  loading="lazy"
                  alt={title}
                />
            }
          </div>
        </div>
      </Link>
      <Link to={`${data.get('path', '')}?filter-set=yoga`}>
        <H6 className="yoga-teachers-page__tile-title">
          {title}
        </H6>
      </Link>
      <p className="yoga-teachers-page__tile-styles">{yogaStyles}</p>
    </div>
  )
}

/**
 * The yoga teachers page tiles component
 * @param {String} props.language - The user's language
 * @param {Array} props.titleGroupIds - The array of title group ids
 * @returns {JSX.Element} tiles component
 */
function YogaTeachersPageTiles (props) {
  const {
    language,
    titleGroupIds,
  } = props

  const { data, loading } = useQuery(GET_PERSONS, {
    variables: {
      language: [language, '_primary'],
      titleGroupIds,
      imageType: PERSON_IMAGE_LIST,
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  })

  const contentList = _get(data, ['contentList', 'content'], []) || []
  const orderedList = getOrderedContent(titleGroupIds, contentList, 'titleGroupId')
  const immutableContentList = fromJS(orderedList) || List()

  if (loading) {
    return <Sherpa type={TYPE_LARGE} />
  }

  return (
    <div className="yoga-teachers-page__tiles">
      {immutableContentList.map((item) => (
        <div key={item.get('id')} className="yoga-teachers-page__tiles-row-item">
          <YogaTeachersPageTile data={getFormattedContentData(item)} />
        </div>
      ))}
    </div>
  )
}

export default YogaTeachersPageTiles
